import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setExperienceLive, setSubmitting } from '../../store/slices/exhibitSlice';
import TcButton from '../TcButton';
import TcPaper from '../TcPaper';
import TcImage from '../TcImage';
import { useSimpleImagePreloader } from '../../hooks/useImagePreloader';
import LoadingScreen from '../shared/LoadingScreen';
import TcScanQrCodeBar from '../TcScanQrCodeBar';

import profileDefault from '../../assets/images/profile-picture-default.png';
import imageBookOfMyths00 from '../../assets/images/exhibit/book-of-myths/img_book-of-myths_00.jpg';

export default function BookOfMyths() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { name, selfieUrl } = useAppSelector(state => state.user);
    const { isSubmitting, experienceLive } = useAppSelector(state => state.exhibit);
    
    const [step, setStep] = useState<'intro' | 'submission' | 'experience-live'>('intro');

    // Preload all images when component mounts
    const allImages = [
        imageBookOfMyths00
    ];

    const { isLoading, isComplete } = useSimpleImagePreloader(allImages);
    
    const handleSubmit = () => {
        dispatch(setSubmitting(true));
        setStep('submission');
        
        // Simulate API call to submit visitor data
        setTimeout(() => {
            dispatch(setSubmitting(false));
            dispatch(setExperienceLive(true));
            setStep('experience-live');
        }, 3000);
    };

    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading images..." />;
    }
    
    // Introduction screen
    if (isComplete && step === 'intro') {
        return (
            <div className="main-content-wrapper bg-black">
                <TcPaper classNameStyle="-mb-6">
                    <h1>{t('exhibits.bookOfMyths.title')}</h1>
                </TcPaper>
                
                {/* Profile Image */}
                <div className="relative mb-8 flex justify-center">
                    {/* Background book image */}
                    <div className="relative">
                        <img 
                            src={imageBookOfMyths00}
                            alt={t('exhibits.bookOfMyths.title')} 
                            className="w-full max-w-md mx-auto"
                        />
                        
                        {/* Profile image overlay */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            {/* Profile image */}
                            <div className="w-32 h-32 rounded-full overflow-hidden border-2 shadow-2xl" style={{ borderColor: '#edcb7e' }}>
                                <img
                                    src={selfieUrl || profileDefault}
                                    alt="Profile"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                        
                        {/* User Name overlapping on book */}
                        {name && (
                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-center">
                                <h2 className="text-5xl font-black tracking-wider uppercase" style={{ color: '#edcb7e', textShadow: '4px 4px 8px rgba(0, 0, 0, 0.8), 2px 2px 4px rgba(0, 0, 0, 0.6)' }}>
                                    {name}
                                </h2>
                            </div>
                        )}
                    </div>
                </div>
                
                <TcPaper classNameStyle="general-content px-6">
                    <p className="mb-6" 
                        dangerouslySetInnerHTML={{ __html: t('exhibits.bookOfMyths.description') }}
                    />
                </TcPaper>
                
                <div className="button-wrapper px-6">
                    <TcButton 
                        handleClick={handleSubmit}
                        disabled
                    >
                        {t('common.comingSoon')}
                    </TcButton>
                </div>

                <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
            </div>
        );
    }
    
    // Submission screen - when user is submitting data
    if (step === 'submission') {
        return (
            <div className="main-content-wrapper !px-6 bg-black">
                <TcPaper classNameStyle="mb-6">
                    <h1>{t('common.leaveYourMark')}</h1>
                </TcPaper>
                
                <div className="mb-6">
                    <img 
                        src={selfieUrl || "/images/default-avatar.png"} 
                        alt="Your Selfie" 
                        className="w-48 h-48 object-cover mx-auto rounded-full border-4 border-blue-500"
                    />
                </div>
                
                <TcPaper classNameStyle="general-content">
                    <p className="text-xl mb-8">
                        {t('exhibits.bookOfMyths.hello', { name: name || t('common.welcome') })}
                    </p>
                    
                    <p className="mb-6">
                        {t('exhibits.bookOfMyths.yourImage')}
                    </p>
                </TcPaper>
                
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
                    <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '80%' }}></div>
                </div>
                
                <p className="text-sm text-gray-500">{t('exhibits.bookOfMyths.processing')}</p>
            </div>
        );
    }
    
    // Experience live screen - when submission is complete
    return (
        <div className="main-content-wrapper !px-6 bg-black">
            <TcPaper classNameStyle="mb-6">
                <h1>{t('exhibits.bookOfMyths.partOfLegend')}</h1>
            </TcPaper>
            
            <div className="mb-6 relative">
                <img 
                    src="/images/book-of-myths-wall.jpg" 
                    alt="Wall Display" 
                    className="w-full rounded-lg"
                />
                <div className="absolute bottom-4 right-4 w-24 h-24 rounded-full overflow-hidden border-2 border-white">
                    <img 
                        src={selfieUrl || "/images/default-avatar.png"} 
                        alt="Your Selfie" 
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>
            
            <TcPaper classNameStyle="general-content">
                <p className="text-xl mb-6">
                    {t('exhibits.bookOfMyths.storyLive')}
                </p>
                
                <p className="mb-8">
                    {t('exhibits.bookOfMyths.exhibitInfo')}
                </p>
            </TcPaper>
            
            <div className="button-wrapper">
                <TcButton 
                    handleClick={() => navigate('/')}
                    primary
                >
                    {t('common.continueExploring')}
                </TcButton>
            </div>
        </div>
    );
} 